import type * as React from "react";
import { useTranslation } from "react-i18next";
import type { PaymentMethod, PaymentState } from "@/lib/endpoints";
import { TONE } from "@/features/invoices/DocumentStatusBadge";
import { Badge } from "@/components/ui/badge";

/**
 * How much of a bill has been received — as a word, BESIDE the document status
 * and never instead of it (§17.5).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THIS IS NOT A DOCUMENT STATUS AND MUST NEVER BE MISTAKEN FOR ONE.
 *
 * `DocumentStatusBadge` renders `ISSUED` / `CANCELLED` — facts about the tax
 * invoice, its legal state, the thing §17.3 exists to protect from being
 * mutated. This renders a fact about the BANK. They are different questions
 * about the same row and both belong on screen at once: a bill is ISSUED *and*
 * partly paid, and "Partly paid" replacing "Issued" would delete the only word
 * that says the document stands.
 *
 * So this badge is only ever rendered NEXT TO the status badge, never in its
 * place, and it deliberately does not extend `DocumentStatusBadge`'s `STATUS`
 * map: one map holding both would be exactly the overloading that §17.5 refuses
 * in the database, reproduced in the client.
 *
 * IT REUSES `TONE`, WHICH IS THE OTHER HALF OF THE SAME DECISION. Two badges
 * side by side must be the same KIND of object — same pill, same weights, same
 * greens — or the eye reads one as a label and the other as decoration. A second
 * green invented here would drift from that one the first time either was
 * touched. Colour is never the only signal (§11.1): the word is always there.
 *
 * THERE IS NO "OVERDUE" TONE AND THERE MUST NOT BE. This system models no credit
 * terms and stores no due date, so nothing here can say a bill is late without
 * the app inventing an agreement the business never made. `UNPAID` is amber
 * ("waiting on somebody else — nothing is wrong yet"), not red.
 * ═════════════════════════════════════════════════════════════════════════════
 */

const STATE: Record<PaymentState, { key: string; tone: keyof typeof TONE }> = {
  /* Money is owed and none has come in. Waiting on the customer, not broken. */
  UNPAID: { key: "status.unpaid", tone: "waiting" },
  /*
   * THE CASE AN ENUM COULD NOT HOLD, which is the whole argument of §17.5:
   * "₹50,000 received against ₹1,45,600" is the ordinary shape of this trade.
   * The word says THAT it happened; the figures beside it, from the server, say
   * how much — which is the only part anyone needs.
   */
  PARTLY_PAID: { key: "status.partlyPaid", tone: "waiting" },
  /* Settled. The document is untouched by it. */
  PAID: { key: "status.paid", tone: "good" },
};

export function PaymentStateBadge({
  state,
  className,
}: {
  state: PaymentState;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const entry = STATE[state];

  return (
    <Badge variant="outline" className={`${TONE[entry.tone]} ${className ?? ""}`}>
      {t(entry.key)}
    </Badge>
  );
}

/** The same word on its own, for an `aria-label` or a table cell. */
export function usePaymentStateLabel(): (state: PaymentState) => string {
  const { t } = useTranslation();
  return (state: PaymentState) => t(STATE[state].key);
}

/**
 * How the money arrived, in the owner's language.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NEFT, RTGS, IMPS AND UPI ARE NOT TRANSLATED, IN EITHER LOCALE, AND THAT IS
 * DELIBERATE. They are the names printed on his bank statement and spoken in the
 * shop; a "translated" NEFT would be a word he then has to map back to the line
 * he is reconciling against. CASH and CHEQUE are ordinary words and do get
 * translated, because those are said in Marathi.
 *
 * `TDS` IS IN THIS LIST AND IT IS NOT A BANK TRANSFER. Tax deducted at source
 * never reaches the account — but the customer HAS discharged it, and it settles
 * the invoice exactly as a NEFT does. Without it every bill a customer deducted
 * 2% from would stay permanently short of its grand total, the overpayment guard
 * would never let it reach zero, and "who owes me money" would fill up with
 * amounts nobody owes. The certificate number goes in the reference field.
 *
 * THE KEYS ARE SPELLED OUT RATHER THAN BUILT AS `` t(`payment.method.${m}`) ``.
 * A key assembled from a variable cannot be found by grepping for it, and the
 * locale check in `src/__tests__` scans for literal `t("…")` calls — a template
 * would hide all eight strings from it. The `Record<PaymentMethod, …>` also
 * fails to compile the day `PAYMENT_METHODS` grows a value, which is exactly
 * when a label is most likely to be forgotten.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const METHOD_KEY: Record<PaymentMethod, string> = {
  CASH: "payment.method.cash",
  CHEQUE: "payment.method.cheque",
  NEFT: "payment.method.neft",
  RTGS: "payment.method.rtgs",
  IMPS: "payment.method.imps",
  UPI: "payment.method.upi",
  TDS: "payment.method.tds",
  OTHER: "payment.method.other",
};

export function usePaymentMethodLabel(): (method: PaymentMethod) => string {
  const { t } = useTranslation();
  return (method: PaymentMethod) => t(METHOD_KEY[method]);
}
